import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router"
import { ChartBar, Speedometer, Stack } from "@phosphor-icons/react"
import { Module } from "~/components/design-system/pages"
import { Badge } from "~/components/design-system/components"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import NotFound from "~/components/reusable/NotFound"
import { useFlags } from "~/features/feature-flags"
import EditFiscalYearTargetButton from "~/components/budget/EditFiscalYearTargetButton"
import { formatFiscalYear } from "~/utils/fiscalCalendar"

// Pathless layout so the fiscal year's tabs wrap only its own pages — a budget under
// /budgets/$fiscalYear/$budgetId brings its own Module and must not nest inside this one.
export const Route = createFileRoute("/_auth/budgets/_layout/$fiscalYear/_layout")({
  component: RouteComponent,
})

const activeTabCrumb = (pathname: string, showBreakdown: boolean): string | null => {
  if (pathname.endsWith("/collections")) return "Collections"
  if (showBreakdown && pathname.endsWith("/breakdown")) return "Budget breakdown"
  return null
}

// TODO: scope both tabs to this fiscal year's date range + collections.
function RouteComponent() {
  const { fiscalYear } = Route.useParams()
  const { pathname } = useLocation()
  const { budget: budgetFlag, budgetUnreleased } = useFlags()

  const tabs: HeaderTab[] = [
    { label: "Overview", icon: <Speedometer />, path: "/budgets/$fiscalYear" },
    ...(budgetUnreleased
      ? [
          {
            label: "Budget breakdown",
            icon: <ChartBar />,
            path: "/budgets/$fiscalYear/breakdown",
          } satisfies HeaderTab,
        ]
      : []),
    { label: "Collections", icon: <Stack />, path: "/budgets/$fiscalYear/collections" },
  ]

  const activeCrumb = activeTabCrumb(pathname, !!budgetUnreleased)
  const breadcrumbItems = [
    { label: "Budgets", href: "/budgets" },
    // On a tab the FY crumb links back to its overview; on the overview it's the last crumb.
    { label: formatFiscalYear(fiscalYear), href: `/budgets/${fiscalYear}` },
    ...(activeCrumb ? [{ label: activeCrumb }] : []),
  ]

  if (budgetFlag === false) return <NotFound />

  return (
    <Module
      breadcrumb={
        <>
          <BreadcrumbWrapper items={breadcrumbItems} />
          <Badge size="sm" variant="violet">
            Beta
          </Badge>
        </>
      }
      tabs={{
        items: tabs,
        path: "/budgets/$fiscalYear",
        actions: <EditFiscalYearTargetButton year={fiscalYear} />,
      }}
    >
      <Outlet />
    </Module>
  )
}
